import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Title from "../common/Title";
import EnhancedGrid from "../common/EnhancedGrid";
import { LuVariable } from "react-icons/lu";
import { FaDatabase } from "react-icons/fa";
import Utils from "../../utils";
import { clientVariableActions } from "../../actions/clientVariable.actions";
import { clientVariableLineActions } from "../../actions/clientVariableLine.actions";
import { clientVariableLineService } from "../../services/clientVariableLine.service";
import VariableGridFilter from "./VariableGridFilter";
import PicklistValueSelect from "./PicklistValueSelect";
import EditPicklistValuesModal from "./EditPicklistValuesModal";
import { MdEdit, MdDelete, MdAdd, MdUpload, MdDownload, MdCheck, MdClose } from "react-icons/md";
import { CiTextAlignJustify } from "react-icons/ci";
import { Alert, Modal, Form, Button, Spinner, ProgressBar, InputGroup } from "react-bootstrap";
import GridHelper from "../helpers/gridHelper";
import ConfirmModal from "../common/ConfirmModal";
import Joyride, { STATUS } from "react-joyride";
import { useTour } from "../../contexts/TourContext";
import { usePermissions } from "../../hooks/usePermissions";
import { useToast } from "../../contexts/ToastContext";

function VariableGrid(props) {
  const { hideTitle, onAddVariable, onEditVariable } = props;
  const { hasPermission } = usePermissions(props.user);
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const clientVariable = useSelector((state) => state.clientVariable);
  const clientVariableLine = useSelector((state) => state.clientVariableLine);
  const { activeTour, endTour } = useTour();
  const [runTour, setRunTour] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);

  const filterInitialState = {
    variableId: "",
    variableName: "",
  };
  const [filter, setFilter] = useState(filterInitialState);
  const [selectedVariable, setSelectedVariable] = useState(null);
  const [showTray, setShowTray] = useState(false);
  const [confirmDeleteModalShow, setConfirmDeleteModalShow] = useState(false);
  const [deleteVariableLineId, setDeleteVariableLineId] = useState("");
  const [addModalShow, setAddModalShow] = useState(false);
  const [editModalShow, setEditModalShow] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [editForm, setEditForm] = useState({
    variableKey: "",
    variableValue: "",
  });
  // #2608: picklist values are owned by this one line (no separate, reusable, named Picklist),
  // and saved independently of the main Key/Value save via EditPicklistValuesModal. Only
  // meaningful for the Edit modal -- adding a new line doesn't hand back a VariableLineId to
  // attach values to yet.
  const [editLinePicklistValues, setEditLinePicklistValues] = useState([]);
  const [showEditPicklistModal, setShowEditPicklistModal] = useState(false);
  // #2610: which control to render for the value being edited (Text/Picklist/Date).
  const [editLineType, setEditLineType] = useState("Text");
  // #2618: whether this line's value can be overridden for a single manual execution (see
  // #2619) without changing the stored value.
  const [editIsRuntimeOverridable, setEditIsRuntimeOverridable] = useState(false);
  const [addForm, setAddForm] = useState({
    variableKey: "",
    variableValue: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [lineSearch, setLineSearch] = useState("");
  const [allVariableLines, setAllVariableLines] = useState([]);
  const fetchIdRef = useRef(0); // Track current fetch to prevent race conditions
  const [loadingAllLines, setLoadingAllLines] = useState(false);
  const [selectedLine, setSelectedLine] = useState(null);
  const [massImportModalShow, setMassImportModalShow] = useState(false);
  const [massImportData, setMassImportData] = useState("");
  const [massImportLoading, setMassImportLoading] = useState(false);
  const [massImportError, setMassImportError] = useState("");
  const [selectedLines, setSelectedLines] = useState([]);
  const [massDeleteLoading, setMassDeleteLoading] = useState(false);
  const [massDeleteProgress, setMassDeleteProgress] = useState({ current: 0, total: 0 });
  const [confirmMassDeleteModalShow, setConfirmMassDeleteModalShow] = useState(false);

  // Inline editing of just the Value cell, directly in the grid -- Key changes and picklist
  // management still require the full Edit modal. Saves straight via the service (not Redux)
  // so each row's in-progress edit stays local to that row rather than racing the shared
  // clientVariableLine.loading/success flags the modal relies on.
  const [inlineEditLineId, setInlineEditLineId] = useState(null);
  const [inlineEditValue, setInlineEditValue] = useState("");
  const [inlinePicklistValues, setInlinePicklistValues] = useState([]);
  const [inlinePicklistLoading, setInlinePicklistLoading] = useState(false);
  const [inlineSaving, setInlineSaving] = useState(false);
  // #2610: which control to render for the cell being inline-edited (Text/Picklist/Date).
  const [inlineLineType, setInlineLineType] = useState("Text");

  const variableActions = [];

  const handleVariableRowClick = (variable) => {
    // Toggle selection: if same variable clicked, deselect it
    if (selectedVariable?.variableId === variable.variableId) {
      setSelectedVariable(null);
      setShowTray(false);
      setLineSearch("");
      setAllVariableLines([]);
      setSelectedLine(null);
      setSelectedLines([]);
    } else {
      setSelectedVariable(variable);
    }
  };

  const handleViewLines = () => {
    if (selectedVariable) {
      setShowTray(true);
      setLineSearch("");
      fetchAllVariableLines(selectedVariable.variableId);
    }
  };

  const handleCloseTray = () => {
    setShowTray(false);
    setSelectedVariable(null);
    setLineSearch(""); // Clear search when closing tray
    setAllVariableLines([]); // Clear loaded lines when closing tray
    setSelectedLine(null); // Clear selected line when closing tray
    setSelectedLines([]); // Clear selected lines for mass delete when closing tray
  };

  const fetchAllVariableLines = async (variableId) => {
    // Increment fetch ID to invalidate any in-progress fetches
    const currentFetchId = ++fetchIdRef.current;

    // Clear existing data immediately to prevent stale data display
    setAllVariableLines([]);
    setLoadingAllLines(true);

    let allLines = [];
    let currentPage = 1; // API uses 1-based page numbers
    let totalPages = 1;

    try {
      // Fetch first page immediately.
      // order_by is required here: without it, the backend's Skip/Take pagination has no
      // deterministic ordering, so separate page fetches can return overlapping rows (dupes)
      // while other rows are never returned at all (see ClientVariableLineRepository.SelectAsync).
      const firstPageParams = {
        variableId: variableId,
        clientId: props.user.clientId,
        clientGuid: props.user.clientGuid,
        pageNumber: 1,
        order_by: "VariableLineId"
      };

      const firstResponse = await clientVariableLineService.get(firstPageParams);

      // Check if this fetch is still current
      if (currentFetchId !== fetchIdRef.current) return;

      const firstPageData = firstResponse.data?.data || [];
      allLines = [...firstPageData];
      totalPages = firstResponse.data?.totalPages || 1;

      // Update UI immediately with first page
      setAllVariableLines(allLines);
      setLoadingAllLines(false);

      // If there are more pages, fetch them in the background
      if (totalPages > 1) {
        currentPage = 2; // Start from page 2 since we already have page 1
        while (currentPage <= totalPages) {
          // Check if this fetch is still current before each page
          if (currentFetchId !== fetchIdRef.current) return;

          const queryParams = {
            variableId: variableId,
            clientId: props.user.clientId,
            clientGuid: props.user.clientGuid,
            pageNumber: currentPage,
            order_by: "VariableLineId"
          };

          const response = await clientVariableLineService.get(queryParams);

          // Check again after async call
          if (currentFetchId !== fetchIdRef.current) return;

          const pageData = response.data?.data || [];
          allLines = [...allLines, ...pageData];

          // Update UI as each page loads
          setAllVariableLines([...allLines]);
          currentPage++;
        }
      }
    } catch (error) {
      // Only update state if this fetch is still current
      if (currentFetchId === fetchIdRef.current) {
        setAllVariableLines([]);
        setLoadingAllLines(false);
      }
    }
  };


  useEffect(() => {
    let queryParams = Utils.getQueryStringParams(location);
    queryParams["clientId"] = props.user.clientId;
    queryParams["clientGuid"] = props.user.clientGuid;

    dispatch(clientVariableActions.get(queryParams));

    // Initialize filter from URL
    setFilter({
      variableId: queryParams["variableId"],
      variableName: queryParams["variableName"],
    });

    // eslint-disable-next-line
  }, [location]);

  // Refresh variable lines when update/create/delete succeeds
  useEffect(() => {
    if (clientVariableLine.success && selectedVariable) {
      // Reload ALL variable lines for the currently selected variable
      fetchAllVariableLines(selectedVariable.variableId);
    }
    // eslint-disable-next-line
  }, [clientVariableLine.success]);

  const handleClearFilters = () => {
    GridHelper.handleSearch(filterInitialState, location, navigate);
  };

  const handleDeleteVariableLine = () => {
    setConfirmDeleteModalShow(false);
    let requestParameters = {
      variableLineId: deleteVariableLineId,
      clientid: props.user.clientId,
      clientGuid: props.user.clientGuid,
    };

    dispatch(clientVariableLineActions.remove(requestParameters));
    showToast("Variable line deleted successfully", "success");
    setSelectedLine(null);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveEdit = () => {
    setSubmitted(true);

    if (editForm.variableKey && editingLine) {
      const updateData = {
        variableId: editingLine.variableId,
        variableLineId: editingLine.variableLineId,
        variableKey: editForm.variableKey,
        variableValue: editForm.variableValue,
        clientId: props.user.clientId,
        clientGuid: props.user.clientGuid,
      };

      dispatch(clientVariableLineActions.update(updateData));

      setEditModalShow(false);
      setEditingLine(null);
      setSubmitted(false);
    }
  };

  const handleCancelEdit = () => {
    setEditModalShow(false);
    setEditingLine(null);
    setEditForm({
      variableKey: "",
      variableValue: "",
    });
    setSubmitted(false);
  };

  const startInlineEdit = (line) => {
    setInlineEditLineId(line.variableLineId);
    setInlineEditValue(line.variableValue || "");
    setInlinePicklistValues([]);
    setInlineLineType("Text");
    setInlinePicklistLoading(true);
    clientVariableLineService.getPicklistValues({ variableLineId: line.variableLineId }).then(
      (response) => {
        setInlinePicklistValues(response.data || []);
        setInlinePicklistLoading(false);
      },
      () => {
        setInlinePicklistValues([]);
        setInlinePicklistLoading(false);
      }
    );
    clientVariableLineService.getLineType({ variableLineId: line.variableLineId }).then(
      (response) => setInlineLineType(response.data || "Text"),
      () => setInlineLineType("Text")
    );
  };

  const cancelInlineEdit = () => {
    setInlineEditLineId(null);
    setInlineEditValue("");
    setInlinePicklistValues([]);
    setInlineLineType("Text");
  };

  const saveInlineEdit = (line) => {
    setInlineSaving(true);
    clientVariableLineService
      .update({
        variableId: line.variableId,
        variableLineId: line.variableLineId,
        variableKey: line.variableKey,
        variableValue: inlineEditValue,
        clientId: props.user.clientId,
        clientGuid: props.user.clientGuid,
      })
      .then(
        () => {
          setInlineSaving(false);
          setInlineEditLineId(null);
          showToast("Variable value updated successfully", "success");
          fetchAllVariableLines(line.variableId);
        },
        () => {
          setInlineSaving(false);
          showToast("Failed to update variable value", "danger");
        }
      );
  };

  const refreshEditLinePicklistValues = (variableLineId) => {
    if (variableLineId) {
      clientVariableLineService.getPicklistValues({ variableLineId }).then(
        (response) => setEditLinePicklistValues(response.data || []),
        () => setEditLinePicklistValues([])
      );
    }
  };

  const refreshEditLineType = (variableLineId) => {
    if (variableLineId) {
      clientVariableLineService.getLineType({ variableLineId }).then(
        (response) => setEditLineType(response.data || "Text"),
        () => setEditLineType("Text")
      );
    }
  };

  const handleEditLineTypeChange = (newType) => {
    const previousType = editLineType;
    setEditLineType(newType);
    clientVariableLineService
      .setLineType({
        variableLineId: editingLine.variableLineId,
        lineType: newType,
        clientId: props.user.clientId,
        clientGuid: props.user.clientGuid,
      })
      .then(
        () => showToast("Value type updated successfully", "success"),
        () => {
          setEditLineType(previousType);
          showToast("Failed to update value type", "danger");
        }
      );
  };

  const refreshEditRuntimeOverridable = (variableLineId) => {
    if (variableLineId) {
      clientVariableLineService.getRuntimeOverridable({ variableLineId }).then(
        (response) => setEditIsRuntimeOverridable(!!response.data),
        () => setEditIsRuntimeOverridable(false)
      );
    }
  };

  const handleEditRuntimeOverridableChange = (newValue) => {
    const previousValue = editIsRuntimeOverridable;
    setEditIsRuntimeOverridable(newValue);
    clientVariableLineService
      .setRuntimeOverridable({
        variableLineId: editingLine.variableLineId,
        isRuntimeOverridable: newValue,
        clientId: props.user.clientId,
        clientGuid: props.user.clientGuid,
      })
      .then(
        () => showToast("Runtime override setting updated successfully", "success"),
        () => {
          setEditIsRuntimeOverridable(previousValue);
          showToast("Failed to update runtime override setting", "danger");
        }
      );
  };

  // Load this line's picklist values, type, and runtime-override setting once the Edit modal
  // is opened for it.
  useEffect(() => {
    if (editModalShow && editingLine) {
      refreshEditLinePicklistValues(editingLine.variableLineId);
      refreshEditLineType(editingLine.variableLineId);
      refreshEditRuntimeOverridable(editingLine.variableLineId);
    }
    // eslint-disable-next-line
  }, [editModalShow, editingLine]);

  const handleAddFormChange = (field, value) => {
    setAddForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveAdd = () => {
    setSubmitted(true);

    if (addForm.variableKey && selectedVariable) {
      const createData = {
        variableId: selectedVariable.variableId,
        variableNumber: selectedVariable.variableNumber,
        variableKey: addForm.variableKey,
        variableValue: addForm.variableValue,
        clientId: props.user.clientId,
        clientGuid: props.user.clientGuid,
      };

      dispatch(clientVariableLineActions.create(createData));
      setAddModalShow(false);
      setAddForm({
        variableKey: "",
        variableValue: ""
      });
      setSubmitted(false);
    }
  };

  const handleCancelAdd = () => {
    setAddModalShow(false);
    setAddForm({
      variableKey: "",
      variableValue: ""
    });
    setSubmitted(false);
  };

  const [massImportProgress, setMassImportProgress] = useState({ current: 0, total: 0 });

  const handleMassImport = async () => {
    setMassImportError("");
    setMassImportProgress({ current: 0, total: 0 });

    // Parse the input - accept either CSV format (key,value per line) or JSON array
    let values = [];
    const trimmedData = massImportData.trim();

    if (!trimmedData) {
      setMassImportError("Please enter data to import");
      return;
    }

    try {
      // Try to parse as JSON first
      if (trimmedData.startsWith("[")) {
        values = JSON.parse(trimmedData);
        if (!Array.isArray(values)) {
          throw new Error("JSON must be an array");
        }
      } else {
        // Parse as CSV (key,value per line)
        const lines = trimmedData.split("\n");
        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine) continue;

          const commaIndex = trimmedLine.indexOf(",");
          if (commaIndex === -1) {
            throw new Error(`Invalid line format: "${trimmedLine}". Expected: key,value`);
          }

          const key = trimmedLine.substring(0, commaIndex).trim();
          const value = trimmedLine.substring(commaIndex + 1).trim();

          if (!key) {
            throw new Error(`Key cannot be empty in line: "${trimmedLine}"`);
          }

          values.push({ key, value });
        }
      }

      if (values.length === 0) {
        setMassImportError("No valid data found to import");
        return;
      }

      setMassImportLoading(true);
      setMassImportProgress({ current: 0, total: values.length });

      // Import each line individually using the existing create endpoint
      let successCount = 0;
      let errors = [];

      for (let i = 0; i < values.length; i++) {
        const item = values[i];
        try {
          const createData = {
            variableId: selectedVariable.variableId,
            variableNumber: selectedVariable.variableNumber,
            variableKey: item.key,
            variableValue: item.value,
            clientId: props.user.clientId,
            clientGuid: props.user.clientGuid,
          };

          await clientVariableLineService.create(createData);
          successCount++;
        } catch (err) {
          errors.push(`Failed to import "${item.key}": ${err.response?.data?.message || err.message || "Unknown error"}`);
        }
        setMassImportProgress({ current: i + 1, total: values.length });
      }

      if (errors.length > 0 && successCount === 0) {
        // All failed
        setMassImportError(`Import failed. ${errors[0]}`);
      } else if (errors.length > 0) {
        // Partial success
        setMassImportError(`Imported ${successCount} of ${values.length} items. ${errors.length} failed.`);
        // Still refresh to show what was imported
        fetchAllVariableLines(selectedVariable.variableId);
      } else {
        // All succeeded - close modal and refresh
        setMassImportModalShow(false);
        setMassImportData("");
        fetchAllVariableLines(selectedVariable.variableId);
      }
    } catch (error) {
      if (error.response?.data?.message) {
        setMassImportError(error.response.data.message);
      } else if (error.message) {
        setMassImportError(error.message);
      } else {
        setMassImportError("An error occurred during import");
      }
    } finally {
      setMassImportLoading(false);
      setMassImportProgress({ current: 0, total: 0 });
    }
  };

  const handleCancelMassImport = () => {
    setMassImportModalShow(false);
    setMassImportData("");
    setMassImportError("");
  };

  const handleMassExport = () => {
    if (!allVariableLines || allVariableLines.length === 0) return;

    // Build CSV content
    const csvLines = allVariableLines.map(line => {
      // Escape values that contain commas, quotes, or newlines
      const escapeCSV = (value) => {
        if (value === null || value === undefined) return "";
        const str = String(value);
        if (str.includes(",") || str.includes('"') || str.includes("\n") || str.includes("\r")) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };
      return `${escapeCSV(line.variableKey)},${escapeCSV(line.variableValue)}`;
    });

    // Add header row
    const csvContent = "key,value\n" + csvLines.join("\n");

    // Create and download the file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${selectedVariable?.variableName || "variable"}_lines.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleToggleLineSelection = (line) => {
    setSelectedLines(prev => {
      const isSelected = prev.some(l => l.variableLineId === line.variableLineId);
      if (isSelected) {
        return prev.filter(l => l.variableLineId !== line.variableLineId);
      } else {
        return [...prev, line];
      }
    });
  };

  const handleSelectAllLines = () => {
    if (selectedLines.length === variableLineData.length) {
      // Deselect all
      setSelectedLines([]);
    } else {
      // Select all visible (filtered) lines
      setSelectedLines([...variableLineData]);
    }
  };

  const handleMassDelete = async () => {
    if (selectedLines.length === 0) return;

    setConfirmMassDeleteModalShow(false);
    setMassDeleteLoading(true);
    setMassDeleteProgress({ current: 0, total: selectedLines.length });

    let successCount = 0;
    let errors = [];

    for (let i = 0; i < selectedLines.length; i++) {
      const line = selectedLines[i];
      try {
        const requestParameters = {
          variableLineId: line.variableLineId,
          clientid: props.user.clientId,
          clientGuid: props.user.clientGuid,
        };
        await clientVariableLineService.remove(requestParameters);
        successCount++;
      } catch (err) {
        errors.push(`Failed to delete "${line.variableKey}"`);
      }
      setMassDeleteProgress({ current: i + 1, total: selectedLines.length });
    }

    setMassDeleteLoading(false);
    setMassDeleteProgress({ current: 0, total: 0 });
    setSelectedLines([]);

    // Refresh the list
    if (selectedVariable) {
      fetchAllVariableLines(selectedVariable.variableId);
    }
  };

  useEffect(() => {
    if (activeTour === "variables") {
      setRunTour(true);
      setTourStepIndex(0);
    }
  }, [activeTour]);

  // Automatically advance to next step when tray opens
  useEffect(() => {
    if (runTour && showTray && tourStepIndex === 2) {
      // We're on the grid step (index 2) and tray just opened
      // Give it a moment for the animation, then move to next step
      setTimeout(() => {
        setTourStepIndex(3);
      }, 300);
    }
  }, [showTray, runTour, tourStepIndex]);

  const tourSteps = [
    {
      target: "body",
      content: "Welcome to My Variables! This is where you store reusable data that your runbooks can reference. Let's explore how it works!",
      placement: "center",
      disableBeacon: true,
    },
    {
      target: '[data-tour="variable-filter"]',
      content: "Use these filters to search for specific variables by ID or name.",
    },
    {
      target: '[data-tour="variable-grid"]',
      content: "This grid shows all your variables. Each variable can contain multiple data entries. Select a row and click 'View Lines' to see what's inside!",
      spotlightClicks: true,
      disableBeacon: true,
      hideFooter: !showTray,
      disableOverlayClose: true,
    },
    {
      target: '[data-tour="variable-tray"]',
      content: "Great! Here you can see all the data stored in this variable. You can search, add, edit, and delete entries as needed.",
    },
    {
      target: '[data-tour="add-line-button"]',
      content: "Click here to add a new entry to this variable.",
    },
    {
      target: '[data-tour="search-lines"]',
      content: "Use this search box to quickly find specific entries.",
    },
    {
      target: '[data-tour="lines-table"]',
      content: "All your data entries are shown here. Each entry has a name and value. Use the edit and delete buttons to manage them.",
    },
  ];

  const handleJoyrideCallback = (data) => {
    const { status, index, action, type } = data;

    // Update step index based on the lifecycle event
    if (type === 'step:after') {
      if (action === 'next') {
        setTourStepIndex(index + 1);
      } else if (action === 'prev') {
        setTourStepIndex(index - 1);
      }
    }

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
      endTour("variables");
      setTourStepIndex(0);
    }
  };

  let variableDisplayColumns = ["variableNumber", "variableName"];
  if (props.user.isSuperUser) {
    variableDisplayColumns.push("clientName");
  }

  const variableData = clientVariable.paginatedResult?.data;
  const rawVariableLineData = allVariableLines;

  // Filter variable lines based on search term
  const variableLineData = rawVariableLineData?.filter(line => {
    if (!lineSearch.trim()) return true;
    const searchTerm = lineSearch.toLowerCase();
    return (
      line.variableKey?.toLowerCase().includes(searchTerm) ||
      line.variableValue?.toLowerCase().includes(searchTerm)
    );
  }) || [];

  // Check if user has permission to view variables
  if (!hasPermission("variables.view")) {
    return (
      <>
        {!hideTitle && <Title text="My Variables" icon={<LuVariable className="mb-1" />} />}
        <Alert variant="danger">
          You do not have permission to view variables. Please contact your administrator.
        </Alert>
      </>
    );
  }

  // Variables are scoped to a client — the left menu already disables this page's nav link
  // without one selected, but this page is still directly reachable (bookmark, back button,
  // clearing the client selector while already here), so gate the content itself too instead
  // of rendering filters/actions/grid against no client.
  if (!props.user.clientId) {
    return (
      <>
        {!hideTitle && <Title text="My Variables" icon={<LuVariable className="mb-1" />} />}
        <Alert variant="info">
          Select a client from the menu at the top of the page to see their variables.
        </Alert>
      </>
    );
  }

  return (
    <>
      <Joyride
        steps={tourSteps}
        run={runTour}
        stepIndex={tourStepIndex}
        continuous
        showProgress
        showSkipButton
        callback={handleJoyrideCallback}
        styles={{
          options: {
            primaryColor: "#cf7f00",
            textColor: "#334155",
            backgroundColor: "#ffffff",
            arrowColor: "#ffffff",
            zIndex: 10000,
          },
          buttonNext: {
            backgroundColor: "#cf7f00",
            borderRadius: "0.5rem",
            fontSize: "14px",
            padding: "10px 20px",
          },
          buttonBack: {
            color: "#cf7f00",
            marginRight: "10px",
          },
          buttonSkip: {
            color: "#64748b",
          },
          tooltip: {
            borderRadius: "0.75rem",
            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
          },
          tooltipContent: {
            padding: "20px",
          },
        }}
      />
      <div className="position-relative">
        {!hideTitle && <Title text="My Variables" icon={<LuVariable className="mb-1" />} />}

        <VariableGridFilter filter={filter} onClearFilters={handleClearFilters} />

        {/* Action Buttons Bar */}
        <div className="d-flex gap-2 mb-3 p-3 bg-light rounded">
          {hasPermission("variables.create") && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => (onAddVariable ? onAddVariable() : navigate("add"))}
            >
              Add Variable Header
            </Button>
          )}

          <div className="d-flex gap-2" style={{
               opacity: selectedVariable ? 1 : 0.6,
               transition: 'opacity 0.3s ease'
             }}>
            <Button
              variant="outline-primary"
              size="sm"
              disabled={!selectedVariable || !hasPermission("variables.edit")}
              onClick={() => {
                if (selectedVariable) {
                  if (onEditVariable) {
                    onEditVariable(selectedVariable.variableId);
                  } else {
                    navigate(`edit?variableId=${selectedVariable.variableId}`);
                  }
                }
              }}
              title={!hasPermission("variables.edit") ? "You do not have permission to edit variables" : ""}
            >
              <MdEdit className="mb-1 me-2" />
              Edit Header
            </Button>

            <Button
              variant="outline-primary"
              size="sm"
              disabled={!selectedVariable}
              onClick={handleViewLines}
            >
              <CiTextAlignJustify className="mb-1 me-2" />
              View Lines
            </Button>
          </div>

          {selectedVariable && (
            <div className="ms-auto align-self-center">
              <small className="text-muted">
                Selected: <strong>{selectedVariable.variableName}</strong>
              </small>
            </div>
          )}
        </div>

        {!hasPermission("variables.create") && (
          <Alert variant="warning">
            You do not have permission to create variables.
          </Alert>
        )}

        {/* Variables Grid */}
        <div className="mb-4" data-tour="variable-grid">
          <EnhancedGrid
            gridId="variable"
            data={variableData}
            displayColumns={variableDisplayColumns}
            idColumn={"variableId"}
            actions={variableActions}
            emptyState={{
              icon: <FaDatabase size={80} className="text-muted mb-3" />,
              message: "No variables found",
              subtitle: "Variables store reusable values for your runbooks.",
              action: (
                <Button variant="primary" size="sm" onClick={() => (onAddVariable ? onAddVariable() : navigate("add"))}>
                  Add Variable
                </Button>
              ),
            }}
            pageNumber={(clientVariable.paginatedResult?.pageNumber ?? 1) - 1}
            totalPages={clientVariable.paginatedResult?.totalPages ?? 0}
            loading={clientVariable.loading}
            onPageChange={GridHelper.handlePageClick}
            onSort={GridHelper.handleSortClick}
            onRowClick={handleVariableRowClick}
            selectedRow={selectedVariable}
            useMobileCards={true}
          />
        </div>

        {/* Slide-out Tray */}
        <div className={`variable-tray ${showTray ? "open" : ""}`} data-tour="variable-tray">
          {selectedVariable && (
            <>
              {/* Tray Header */}
              <div className="p-3 border-bottom bg-light d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <CiTextAlignJustify className="me-2" />
                  Variable Lines for "{selectedVariable.variableName}"
                </h5>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleCloseTray}
                >
                  ×
                </button>
              </div>

              {/* Action Buttons Bar */}
              <div className="d-flex gap-2 mb-3 p-3 bg-light border-bottom flex-wrap">
                {hasPermission("variables.create") ? (
                  <>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setAddModalShow(true)}
                      data-tour="add-line-button"
                    >
                      <MdAdd className="mb-1 me-2" />
                      Add
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setMassImportModalShow(true)}
                    >
                      <MdUpload className="mb-1 me-2" />
                      Mass Import
                    </Button>
                  </>
                ) : null}

                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleMassExport}
                  disabled={!allVariableLines || allVariableLines.length === 0}
                >
                  <MdDownload className="mb-1 me-2" />
                  Mass Export
                </Button>

                {hasPermission("variables.delete") && selectedLines.length > 0 && !massDeleteLoading && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => setConfirmMassDeleteModalShow(true)}
                  >
                    <MdDelete className="mb-1 me-2" />
                    Delete Selected ({selectedLines.length})
                  </Button>
                )}

                {massDeleteLoading && (
                  <div className="d-flex align-items-center gap-2" style={{ minWidth: '250px' }}>
                    <div className="flex-grow-1">
                      <div className="small text-muted mb-1">
                        Deleting {massDeleteProgress.current} of {massDeleteProgress.total}...
                      </div>
                      <ProgressBar
                        animated
                        striped
                        variant="danger"
                        now={massDeleteProgress.total > 0 ? (massDeleteProgress.current / massDeleteProgress.total) * 100 : 0}
                        style={{ height: '8px' }}
                      />
                    </div>
                  </div>
                )}

                <div style={{
                     opacity: selectedLine ? 1 : 0.6,
                     transition: 'opacity 0.3s ease',
                     display: 'flex',
                     gap: '0.5rem'
                   }}>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={!selectedLine || !hasPermission("variables.edit")}
                    onClick={() => {
                      if (selectedLine) {
                        setEditingLine(selectedLine);
                        setEditForm({
                          variableKey: selectedLine.variableKey || "",
                          variableValue: selectedLine.variableValue || "",
                        });
                        setEditModalShow(true);
                        setSubmitted(false);
                      }
                    }}
                  >
                    <MdEdit className="mb-1 me-2" />
                    Edit
                  </Button>

                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={!selectedLine || !hasPermission("variables.delete")}
                    onClick={() => {
                      if (selectedLine) {
                        setDeleteVariableLineId(selectedLine.variableLineId);
                        setConfirmDeleteModalShow(true);
                      }
                    }}
                  >
                    <MdDelete className="mb-1 me-2" />
                    Delete
                  </Button>
                </div>

                {selectedLine && (
                  <div className="ms-auto align-self-center">
                    <small className="text-muted">
                      Selected: <strong>{selectedLine.variableKey}</strong>
                    </small>
                  </div>
                )}
              </div>

              {/* Search */}
              <div className="p-3 border-bottom">
                <div className="input-group input-group-sm" data-tour="search-lines">
                  <span className="input-group-text">
                    <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
                    </svg>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by key or value..."
                    value={lineSearch}
                    onChange={(e) => setLineSearch(e.target.value)}
                  />
                  {lineSearch && (
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => setLineSearch("")}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>

              {/* Variable Lines Grid */}
              <div className="p-3" style={{ height: "calc(100vh - 240px)", overflow: "auto" }}>
                {loadingAllLines ? (
                  <div className="text-center py-4">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : variableLineData && variableLineData.length > 0 ? (
                  <>
                    {/* Result count */}
                    <div className="small text-muted mb-2">
                      Showing {variableLineData.length} of {rawVariableLineData?.length || 0} lines
                      {lineSearch.trim() && (
                        <span className="ms-2">
                          (filtered by "{lineSearch}")
                          <button
                            className="btn btn-link btn-sm p-0 ms-1"
                            onClick={() => setLineSearch("")}
                            style={{ fontSize: '12px', textDecoration: 'none' }}
                          >
                            ×
                          </button>
                        </span>
                      )}
                    </div>

                    <div className="table-responsive" data-tour="lines-table">
                      <table className="table table-striped table-hover table-sm">
                        <thead className="table-light sticky-top">
                          <tr>
                            {hasPermission("variables.delete") && (
                              <th width="40px" className="text-center">
                                <Form.Check
                                  type="checkbox"
                                  checked={selectedLines.length === variableLineData.length && variableLineData.length > 0}
                                  onChange={handleSelectAllLines}
                                  title="Select all"
                                />
                              </th>
                            )}
                            <th width="40%">Key</th>
                            <th width="60%">Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {variableLineData.map((line) => (
                            <tr
                              key={line.variableLineId}
                              onClick={() => setSelectedLine(line)}
                              className={selectedLine?.variableLineId === line.variableLineId ? "table-primary" : ""}
                              style={{ cursor: "pointer" }}
                            >
                              {hasPermission("variables.delete") && (
                                <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                  <Form.Check
                                    type="checkbox"
                                    checked={selectedLines.some(l => l.variableLineId === line.variableLineId)}
                                    onChange={() => handleToggleLineSelection(line)}
                                  />
                                </td>
                              )}
                              <td>{line.variableKey}</td>
                              <td onClick={(e) => e.stopPropagation()}>
                                {inlineEditLineId === line.variableLineId ? (
                                  <div className="d-flex align-items-center gap-1" style={{ maxWidth: "400px" }}>
                                    {inlinePicklistLoading ? (
                                      <Spinner size="sm" />
                                    ) : inlineLineType === "Picklist" ? (
                                      <PicklistValueSelect
                                        value={inlineEditValue}
                                        onChange={setInlineEditValue}
                                        options={inlinePicklistValues}
                                        placeholder="Select a value"
                                      />
                                    ) : inlineLineType === "Date" ? (
                                      <>
                                        <Form.Control
                                          size="sm"
                                          type="date"
                                          autoFocus
                                          value={Utils.displayDateToIso(inlineEditValue)}
                                          onChange={(e) => setInlineEditValue(Utils.isoDateToDisplay(e.target.value))}
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter") saveInlineEdit(line);
                                            if (e.key === "Escape") cancelInlineEdit();
                                          }}
                                        />
                                        <Button
                                          variant="outline-secondary"
                                          size="sm"
                                          disabled={!inlineEditValue}
                                          onClick={() => setInlineEditValue("")}
                                          title="Clear date"
                                        >
                                          ×
                                        </Button>
                                      </>
                                    ) : (
                                      <Form.Control
                                        size="sm"
                                        autoFocus
                                        value={inlineEditValue}
                                        onChange={(e) => setInlineEditValue(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") saveInlineEdit(line);
                                          if (e.key === "Escape") cancelInlineEdit();
                                        }}
                                      />
                                    )}
                                    <Button
                                      variant="outline-success"
                                      size="sm"
                                      disabled={inlineSaving || inlinePicklistLoading}
                                      onClick={() => saveInlineEdit(line)}
                                      title="Save"
                                    >
                                      {inlineSaving ? <Spinner size="sm" /> : <MdCheck />}
                                    </Button>
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      disabled={inlineSaving}
                                      onClick={cancelInlineEdit}
                                      title="Cancel"
                                    >
                                      <MdClose />
                                    </Button>
                                  </div>
                                ) : (
                                  <div
                                    className="d-flex align-items-center justify-content-between"
                                    style={{ cursor: hasPermission("variables.edit") ? "text" : "default" }}
                                    onClick={() => hasPermission("variables.edit") && startInlineEdit(line)}
                                  >
                                    <div style={{ wordBreak: "break-word", maxWidth: "300px" }}>
                                      {line.variableValue}
                                    </div>
                                    {hasPermission("variables.edit") && (
                                      <MdEdit className="text-muted ms-2 flex-shrink-0" title="Click to edit value" />
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4 text-muted">
                    {lineSearch.trim() ? (
                      <>
                        <div>No variable lines found matching "{lineSearch}"</div>
                        <button
                          className="btn btn-link btn-sm mt-2"
                          onClick={() => setLineSearch("")}
                        >
                          Clear search to see all lines
                        </button>
                      </>
                    ) : (
                      "No variable lines found"
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Backdrop */}
        {showTray && (
          <div className="variable-tray-backdrop" onClick={handleCloseTray} />
        )}
      </div>

      {/* Add Variable Line Modal */}
      <Modal show={addModalShow} onHide={handleCancelAdd} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <MdAdd className="me-2" />
            Add Variable Line
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {clientVariableLine.error && (
            <Alert variant="danger">
              An error has occurred. Please try again later.
            </Alert>
          )}

          <Form>
            <Form.Group controlId="formAddVariableKey" className="mb-3">
              <Form.Label>Variable Key *</Form.Label>
              <Form.Control
                type="text"
                value={addForm.variableKey}
                onChange={(e) => handleAddFormChange('variableKey', e.target.value)}
                className={submitted && !addForm.variableKey ? "is-invalid" : ""}
                placeholder="Enter variable key"
              />
              <Form.Control.Feedback type="invalid">
                Please provide a variable key.
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="formAddVariableValue" className="mb-3">
              <Form.Label>Variable Value</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={addForm.variableValue}
                onChange={(e) => handleAddFormChange('variableValue', e.target.value)}
                placeholder="Enter variable value"
              />
              <Form.Text className="text-muted">
                You can set a value type (picklist, date) after saving it.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCancelAdd}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveAdd}
            disabled={clientVariableLine.loading}
          >
            {clientVariableLine.loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              "Add Variable Line"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Variable Line Modal */}
      <Modal show={editModalShow} onHide={handleCancelEdit} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <MdEdit className="me-2" />
            Edit Variable Line
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {clientVariableLine.error && (
            <Alert variant="danger">
              An error has occurred. Please try again later.
            </Alert>
          )}

          {editingLine && (
            <Form>
              <Form.Group controlId="formVariableLineId" className="mb-3">
                <Form.Label>Variable Line ID</Form.Label>
                <Form.Control
                  type="text"
                  value={editingLine.variableLineId}
                  disabled
                />
              </Form.Group>

              <Form.Group controlId="formVariableKey" className="mb-3">
                <Form.Label>Variable Key *</Form.Label>
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <Form.Label className="small text-muted">Old Value</Form.Label>
                    <Form.Control
                      type="text"
                      value={editingLine.variableKey}
                      disabled
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <Form.Label className="small text-muted">New Value</Form.Label>
                    <Form.Control
                      type="text"
                      value={editForm.variableKey}
                      onChange={(e) => handleEditFormChange('variableKey', e.target.value)}
                      className={submitted && !editForm.variableKey ? "is-invalid" : ""}
                      placeholder="Enter variable key"
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide a variable key.
                    </Form.Control.Feedback>
                  </div>
                </div>
              </Form.Group>

              <Form.Group controlId="formLineType" className="mb-3">
                <Form.Label>Value Type</Form.Label>
                <Form.Select
                  value={editLineType}
                  onChange={(e) => handleEditLineTypeChange(e.target.value)}
                >
                  <option value="Text">Text</option>
                  <option value="Picklist">Picklist</option>
                  <option value="Date">Date</option>
                </Form.Select>
              </Form.Group>

              <Form.Group controlId="formRuntimeOverridable" className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Allow this value to be overridden for a single manual run"
                  checked={editIsRuntimeOverridable}
                  onChange={(e) => handleEditRuntimeOverridableChange(e.target.checked)}
                />
                <Form.Text className="text-muted">
                  The override only applies to that one execution -- the stored value here is never changed by it.
                </Form.Text>
              </Form.Group>

              <Form.Group controlId="formVariableValue" className="mb-3">
                <Form.Label>Variable Value</Form.Label>
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <Form.Label className="small text-muted">Old Value</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={editingLine.variableValue || ""}
                      disabled
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <Form.Label className="small text-muted">New Value</Form.Label>
                    {editLineType === "Picklist" ? (
                      <PicklistValueSelect
                        value={editForm.variableValue}
                        onChange={(value) => handleEditFormChange('variableValue', value)}
                        options={editLinePicklistValues}
                        placeholder="Select a value"
                      />
                    ) : editLineType === "Date" ? (
                      <InputGroup>
                        <Form.Control
                          type="date"
                          value={Utils.displayDateToIso(editForm.variableValue)}
                          onChange={(e) => handleEditFormChange('variableValue', Utils.isoDateToDisplay(e.target.value))}
                        />
                        <Button
                          variant="outline-secondary"
                          disabled={!editForm.variableValue}
                          onClick={() => handleEditFormChange('variableValue', "")}
                          title="Clear date"
                        >
                          ×
                        </Button>
                      </InputGroup>
                    ) : (
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={editForm.variableValue}
                        onChange={(e) => handleEditFormChange('variableValue', e.target.value)}
                        placeholder="Enter variable value"
                      />
                    )}
                    {editLineType === "Picklist" && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="mt-2"
                        onClick={() => setShowEditPicklistModal(true)}
                      >
                        {editLinePicklistValues.length > 0 ? "Edit Picklist" : "Add Picklist Values"}
                      </Button>
                    )}
                  </div>
                </div>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCancelEdit}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveEdit}
            disabled={clientVariableLine.loading}
          >
            {clientVariableLine.loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {editingLine && editLineType === "Picklist" && (
        <EditPicklistValuesModal
          show={showEditPicklistModal}
          onHide={() => setShowEditPicklistModal(false)}
          variableLineId={editingLine.variableLineId}
          clientId={props.user.clientId}
          clientGuid={props.user.clientGuid}
          initialValues={
            editLinePicklistValues.length > 0
              ? editLinePicklistValues
              : editForm.variableValue
                ? [{ value: editForm.variableValue, displayOrder: 0 }]
                : []
          }
          onSaved={() => refreshEditLinePicklistValues(editingLine.variableLineId)}
        />
      )}

      <ConfirmModal
        show={confirmDeleteModalShow}
        title="Delete Variable Line"
        question={`Are you sure you want to delete variable line ${deleteVariableLineId}?`}
        noButtonText="No"
        yesButtonText="Yes, please"
        handleClose={() => {
          setConfirmDeleteModalShow(false);
        }}
        handleConfirm={() => {
          handleDeleteVariableLine();
        }}
      />

      <ConfirmModal
        show={confirmMassDeleteModalShow}
        title="Delete Multiple Variable Lines"
        question={`Are you sure you want to delete ${selectedLines.length} variable line${selectedLines.length > 1 ? 's' : ''}? This action cannot be undone.`}
        noButtonText="Cancel"
        yesButtonText="Yes, delete them"
        handleClose={() => {
          setConfirmMassDeleteModalShow(false);
        }}
        handleConfirm={() => {
          handleMassDelete();
        }}
      />

      {/* Mass Import Modal */}
      <Modal show={massImportModalShow} onHide={handleCancelMassImport} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <MdUpload className="me-2" />
            Mass Import Variable Lines
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {massImportError && (
            <Alert variant="danger">
              {massImportError}
            </Alert>
          )}

          <Alert variant="info">
            <strong>Instructions:</strong>
            <p className="mb-2">Enter data in one of the following formats:</p>
            <p className="mb-1"><strong>CSV format</strong> (one entry per line):</p>
            <code className="d-block mb-2 p-2 bg-light">
              key1,value1<br/>
              key2,value2<br/>
              key3,value3
            </code>
            <p className="mb-1"><strong>JSON format</strong> (array of objects):</p>
            <code className="d-block p-2 bg-light">
              {'[{"key":"key1","value":"value1"},{"key":"key2","value":"value2"}]'}
            </code>
          </Alert>

          <Form>
            <Form.Group controlId="formMassImportData" className="mb-3">
              <Form.Label>Data to Import *</Form.Label>
              <Form.Control
                as="textarea"
                rows={10}
                value={massImportData}
                onChange={(e) => setMassImportData(e.target.value)}
                placeholder="Enter CSV or JSON data..."
                style={{ fontFamily: "monospace" }}
              />
            </Form.Group>
          </Form>

          {massImportLoading && (
            <div className="mt-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-muted">
                  Importing {massImportProgress.current} of {massImportProgress.total}...
                </span>
                <span className="text-muted">
                  {massImportProgress.total > 0 ? Math.round((massImportProgress.current / massImportProgress.total) * 100) : 0}%
                </span>
              </div>
              <ProgressBar
                animated
                striped
                variant="primary"
                now={massImportProgress.total > 0 ? (massImportProgress.current / massImportProgress.total) * 100 : 0}
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCancelMassImport} disabled={massImportLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleMassImport}
            disabled={massImportLoading || !massImportData.trim()}
          >
            {massImportLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Importing...
              </>
            ) : (
              "Import"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default VariableGrid;
